import { AfterViewInit, Component, input, OnInit, ViewChild, ViewEncapsulation } from '@angular/core';
import { AbstractControl, FormControl, FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';

import { MdbFormsModule } from 'mdb-angular-ui-kit/forms';
import { MdbOptionModule } from 'mdb-angular-ui-kit/option';
import { MdbAutocompleteModule, MdbAutocompleteSelectedEvent } from 'mdb-angular-ui-kit/autocomplete';
import { TranslateModule } from '@ngx-translate/core';
import { MdbValidationModule } from 'mdb-angular-ui-kit/validation';
import { SelectorModel } from '../../../models/table-column.model';
import { debounceTime, distinctUntilChanged, Subject } from 'rxjs';
import { AModel } from '../../../models/resources/a-model';
import { MdbInfiniteScrollModule } from 'mdb-angular-ui-kit/infinite-scroll';
import { AResourceService } from '../../../services/resources/a-resource.service';
import {UrlParamModel} from "../../../models/url-param.model";

@Component({
  selector: 'app-link-select',
  standalone: true,
  encapsulation: ViewEncapsulation.None,
  imports: [
    MdbFormsModule,
    MdbOptionModule,
    MdbAutocompleteModule,
    MdbValidationModule,
    ReactiveFormsModule,
    TranslateModule,
    FormsModule,
    MdbInfiniteScrollModule
],
  templateUrl: './link-select.component.html',
  styleUrls: ['./link-select.component.scss']
})
export class LinkSelectComponent implements OnInit, AfterViewInit {
  id = input.required<string>();
  label = input.required<string>();
  controlName = input.required<string>();
  formGroup = input.required<FormGroup>();
  optionLabelSelector = input.required<SelectorModel[]>();
  optionValueSelector = input<string | undefined>();
  baseLink = input<string>();
  isRequired = input(false);
  hideLabel = input(false);
  placeholder = input<string|undefined>(undefined);
  searchService = input.required<AResourceService<AModel>>();
  searchServiceResourceUrl = input<string>();
  searchValueName = input('text-search');
  queryParams = input<UrlParamModel[]>([]);

  searchControl = new FormControl<string>('');

  currentSearchText = '';
  searchText = new Subject<string>();
  notFound = false;
  loading = false;

  private observer!: MutationObserver;

  constructor() {
    this.searchText.pipe(
      debounceTime(250),
      distinctUntilChanged()
    ).subscribe((value: string) => {
      this.filter(value);
    });
  }

  get selected(): AModel | null {
    return this.formGroup().get(this.controlName())?.value ?? null;
  }

  get selectedLink(): string | null {
    const selected = this.selected;
    if (!selected || !selected.id) return null;
    return `${this.baseLink()}/${selected.id}`;
  }

  ngOnInit(): void {
    this.filter('');
    if(this.control?.value)
      this.searchControl.setValue(this.control.value);
  }

  onSelected(selected: MdbAutocompleteSelectedEvent) {
    this.control?.setValue(selected.option.value)
  }

  filter(value: string, addPage: boolean = false): void {
    // this.loading = true;
    this.currentSearchText = value;

    const dynamicSearchParam: UrlParamModel[] = value
      ? [{ key: this.searchValueName(), value: encodeURIComponent(value) }]
      : [];

    const urlParams = [...dynamicSearchParam, ...this.queryParams()];

    const service = this.searchService();
    if (service) {
      service.getAll({
        urlParams,
        pagination: {
          next: addPage,
        },
        paginated: true,
        addToList: addPage,
        resetPagination: !addPage,
        resetData: !addPage,
      }, this.searchServiceResourceUrl() ?? null);
    }
  }

  getOptionLabel(data: any): string {
    let result = '';
    this.optionLabelSelector().forEach((s: SelectorModel) => {
      let selectorResult = data;
      s.selector.split('.').forEach((sp) => {
        selectorResult = selectorResult?.[sp];
      });
      result += selectorResult ?? '';
      if (s.suffix) result += s.suffix;
    });
    return result;
  }

  displayValue = (value: any): string => {
    return value ? this.getOptionLabel(value) : '';
  };

  get control(): any {
    return this.formGroup()!.get(this.controlName())!;
  }

  ngAfterViewInit(): void {
    this.observer = new MutationObserver(() => {
      const scrollContainer = document.getElementsByClassName('autocomplete-items-list')[0];
      if (scrollContainer) {
        scrollContainer.addEventListener('scroll', () => this.onScroll(scrollContainer));
        this.observer.disconnect();
      }
    });

    this.observer.observe(document.body, {
      childList: true,
      subtree: true
    });
  }

  ngOnDestroy() {
    this.observer.disconnect();
  }

  onInputChange(event: Event) {
    this.control?.setValue(null)
    const input = event.target as HTMLInputElement;
    this.searchText.next(input.value);
  }

  onScroll(element: Element): void {
    const atBottom =
      element.scrollHeight - element.scrollTop <= element.clientHeight + 5;

    if (atBottom && !this.searchService().loading().loading) {
      this.filter(this.currentSearchText, true);
    }
  }
}
